// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { sharedTagStyle } from "@bg/XDL/Utilities/sharedStyles";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetQuotesApiParameter } from "@insite/client-framework/Services/QuoteService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAccountsDataView } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import { getBillTosDataView } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import updateSearchFields from "@insite/client-framework/Store/Pages/RfqMyQuotes/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { DatePickerState } from "@insite/mobius/DatePicker";
import Tag, { horizontalStyles, TagPresentationProps } from "@insite/mobius/Tag";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
    billTosDataView: getBillTosDataView(state, {}),
    accountsDataView: getAccountsDataView(state),
    quotesDataView: getQuotesDataView(state, state.pages.rfqMyQuotes.getQuotesParameter),
    filtersOpen: state.pages.rfqMyQuotes.filtersOpen,
    getQuotesParameter: state.pages.rfqMyQuotes.getQuotesParameter as GetQuotesApiParameter & { poNumber?: string },
});

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyQuotesListTagsStyles {
    appliedFiltersContainer?: InjectableCss;
}

export const myQuotesListFilterStyles: MyQuotesListTagsStyles = {
    appliedFiltersContainer: {
        css: css`
            ${horizontalStyles}
            && div[class*=""TagStyle-sc] {
                width: auto;
                height: auto !important;
            }
        `,
    },
};

const styles = myQuotesListFilterStyles;

let updateTimeoutId: number | undefined;

const RfqMyQuotesFilterTags = ({
    filtersOpen,
    getQuotesParameter,
    updateSearchFields,
    billTosDataView,
    accountsDataView,
    quotesDataView,
}: Props) => {
    if (filtersOpen) {
        return null;
    }

    const fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({ fromDate: "" });
    };

    const toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({ toDate: "" });
    };

    const expireFromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({ expireFromDate: "" });
    };

    const expireToDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({ expireToDate: "" });
    };

    const customerChangeHandler = (customerId: string) => {
        updateSearchFields({ customerId: customerId || "" });
    };

    const getCustomerLabel = (customerId: string) => {
        if (!billTosDataView.value) {
            return "";
        }

        const customer = billTosDataView.value.find(o => o.id === customerId);
        if (!customer) {
            return "";
        }

        return `${customer.customerNumber} - ${customer.customerName}`;
    };

    const quoteNumberChangeHandler = (quoteNumber: string) => {
        if (typeof updateTimeoutId === "number") {
            clearTimeout(updateTimeoutId);
        }

        updateTimeoutId = setTimeout(() => {
            updateSearchFields({
                quoteNumber: "",
            });
        }, 250);
    };

    const poNumberChangeHandler = (poNumber: string) => {
        if (typeof updateTimeoutId === "number") {
            clearTimeout(updateTimeoutId);
        }

        updateTimeoutId = setTimeout(() => {
            updateSearchFields({ poNumber: "" } as any);
        }, 250);
    };

    const userChangeHandler = (userId: string) => {
        updateSearchFields({ userId: "" });
    };

    const salesRepChangeHandler = (salesRepNumber: string) => {
        updateSearchFields({ salesRepNumber: "" });
    };

    const getUserLabel = (userId: string) => {
        if (!accountsDataView.value) {
            return "";
        }

        return accountsDataView.value.find(o => o.id === userId)?.userName || "";
    };

    const getSalesRepLabel = (salesRepNumber: string) => {
        if (!quotesDataView.value || !quotesDataView.salespersonList) {
            return "";
        }

        return quotesDataView.salespersonList.find(o => o.salespersonNumber === salesRepNumber)?.name || "";
    };

    const statusChangeHandler = () => {
        updateSearchFields({ statuses: undefined });
    };

    return (
        <StyledWrapper {...styles.appliedFiltersContainer}>
            {getQuotesParameter.statuses && getQuotesParameter.statuses.length > 0 && (
                <Tag css={sharedTagStyle} onDelete={statusChangeHandler} data-test-selector="rfqMyQuotes_status">
                    {translate("Quote Status: {0}", getQuotesParameter.statuses[0])}
                </Tag>
            )}
            {getQuotesParameter.fromDate && (
                <Tag
                    css={sharedTagStyle}
                    onDelete={() => {
                        fromDateChangeHandler({} as any);
                    }}
                    data-test-selector="rfqMyQuotes_fromDateButton"
                >
                    {translate("Requested From: {0}", getQuotesParameter.fromDate)}
                </Tag>
            )}
            {getQuotesParameter.toDate && (
                <Tag
                    css={sharedTagStyle}
                    onDelete={() => {
                        toDateChangeHandler({} as any);
                    }}
                >
                    {translate("Requested To: {0}", getQuotesParameter.toDate)}
                </Tag>
            )}
            {getQuotesParameter.expireFromDate && (
                <Tag
                    css={sharedTagStyle}
                    onDelete={() => {
                        expireFromDateChangeHandler({} as any);
                    }}
                    data-test-selector="rfqMyQuotes_fromDateButton"
                >
                    {translate("Expires From: {0}", getQuotesParameter.expireFromDate)}
                </Tag>
            )}
            {getQuotesParameter.expireToDate && (
                <Tag
                    css={sharedTagStyle}
                    onDelete={() => {
                        expireToDateChangeHandler({} as any);
                    }}
                >
                    {translate("Expires To: {0}", getQuotesParameter.expireToDate)}
                </Tag>
            )}
            {getQuotesParameter.customerId && (
                <Tag
                    css={sharedTagStyle}
                    onDelete={() => {
                        customerChangeHandler("");
                    }}
                >
                    {translate("Customer: {0}", getCustomerLabel(getQuotesParameter.customerId))}
                </Tag>
            )}
            {getQuotesParameter.quoteNumber && (
                <Tag
                    css={sharedTagStyle}
                    onDelete={() => {
                        quoteNumberChangeHandler("");
                    }}
                >
                    {translate("Quote #: {0}", getQuotesParameter.quoteNumber)}
                </Tag>
            )}
            {getQuotesParameter.poNumber && (
                <Tag
                    css={sharedTagStyle}
                    onDelete={() => {
                        poNumberChangeHandler("");
                    }}
                >
                    {translate("PO/REF: {0}", getQuotesParameter.poNumber)}
                </Tag>
            )}
            {getQuotesParameter.userId && (
                <Tag
                    css={sharedTagStyle}
                    onDelete={() => {
                        userChangeHandler("");
                    }}
                >
                    {translate("User: {0}", getUserLabel(getQuotesParameter.userId))}
                </Tag>
            )}
            {getQuotesParameter.salesRepNumber && (
                <Tag
                    css={sharedTagStyle}
                    onDelete={() => {
                        salesRepChangeHandler("");
                    }}
                >
                    {translate("Sales Rep: {0}", getSalesRepLabel(getQuotesParameter.salesRepNumber))}
                </Tag>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RfqMyQuotesFilterTags),
    definition: {
        group: "XNGAGE" as any,
        displayName: "Tags",
        allowedContexts: ["RfqMyQuotesPage"],
    },
};

export default widgetModule;
